import { type IntlShape } from "react-intl";

import { messages } from "../components/messages.generated";
import {
    LENS_BUNDLE_MAX_BYTES,
    LOGO_ACCEPTED_TYPES,
    LOGO_MAX_BYTES,
    LOGO_MAX_DIMENSION_PX,
    PUSH_ARCHIVE_MAX_BYTES,
    type SubmissionCheck,
    ZIP_MAX_BYTES,
} from "../model";
import { type WebLensBuildOutputMeasurement } from "../utils/measureWebLensBuildOutput";
import { type WebLensLogoMeasurement } from "../utils/measureWebLensLogo";

export const formatMegabytes = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

// Shared with the submission rail's readiness list (readinessChecks.ts) so the row a creator reads
// while filling the form and the row submit() writes can never disagree about the same file.
export const isZipPackage = (file: File): boolean => file.name.toLowerCase().endsWith(".zip");

// Empty archives fail here rather than uploading a zip that can only fail later, during the
// asynchronous conversion.
export const isPackageSizeAccepted = (file: File): boolean => file.size > 0 && file.size <= ZIP_MAX_BYTES;

/**
 * Detail line for the package-type row, shared with the readiness list so the same file never
 * reads differently in the two paths — the row must not change shape the moment submit() takes
 * over from the readiness list.
 *
 * Populated only when the row fails: the dropzone is already the one place that reports a healthy
 * package's name and size, so repeating them under a passing row is noise.
 */
export const describePackageTypeFailure = (intl: IntlShape, file: File): string | undefined =>
    isZipPackage(file) ? undefined : intl.formatMessage(messages.checkPackageTypeFailDetail, { fileName: file.name });

/**
 * The .zip cap row, shown only when it fails. A passing zip-cap row next to the build-output
 * budget row reads as two conflicting size limits; on failure it explains why Submit is off.
 * The measured size is what explains an over-cap rejection, so it is the detail.
 */
export const buildPackageSizeChecks = (intl: IntlShape, file: File): SubmissionCheck[] =>
    isPackageSizeAccepted(file)
        ? []
        : [
              {
                  id: "package-size",
                  label: intl.formatMessage(messages.checkPackageSizeLabel, {
                      limit: formatMegabytes(ZIP_MAX_BYTES),
                  }),
                  state: "fail",
                  detail:
                      file.size === 0
                          ? intl.formatMessage(messages.checkPackageEmptyDetail)
                          : formatMegabytes(file.size),
              },
          ];

/**
 * The budget gate is open whenever there is nothing measured: no file yet, parse still in flight,
 * or an archive the parser bails on (not a zip, zip64, no build output). In every open case the
 * server-side conversion is the verdict; the gate only closes on a MEASURED overshoot, so it can
 * never falsely block an archive we couldn't read.
 */
export const isBuildOutputWithinBudget = (measurement: WebLensBuildOutputMeasurement | null): boolean =>
    !measurement || measurement.compressedBytes <= LENS_BUNDLE_MAX_BYTES;

/**
 * The budget row exists only once a measurement exists — same conditional idiom as the icon rows.
 * Shared by the readiness list and runClientPreChecks so the row cannot change verdict when
 * submit() takes the rail over.
 */
export const buildBudgetCheck = (intl: IntlShape, measurement: WebLensBuildOutputMeasurement): SubmissionCheck => {
    const within = measurement.compressedBytes <= LENS_BUNDLE_MAX_BYTES;
    return {
        id: "package-budget",
        label: intl.formatMessage(messages.checkBudgetLabel, { limit: formatMegabytes(LENS_BUNDLE_MAX_BYTES) }),
        state: within ? "pass" : "fail",
        detail: within
            ? undefined
            : intl.formatMessage(messages.checkBudgetFailDetail, {
                  used: formatMegabytes(measurement.compressedBytes),
              }),
    };
};

const isLogoTypeAccepted = (logo: File): boolean => LOGO_ACCEPTED_TYPES.includes(logo.type);
const isLogoSizeAccepted = (logo: File): boolean => logo.size <= LOGO_MAX_BYTES;

/** Whether an attached icon would survive submit() — the icon itself stays optional. */
export const isLogoAccepted = (logo: File): boolean => isLogoTypeAccepted(logo) && isLogoSizeAccepted(logo);

/**
 * Both icon rows, shared with the readiness list for the same reason as the package rows: an
 * attached icon must read identically before and during submission. Only produced when an icon is
 * attached — the icon is optional, so its absence is not an unfinished step.
 */
export const buildLogoChecks = (intl: IntlShape, logo: File): SubmissionCheck[] => [
    {
        id: "logo-type",
        label: intl.formatMessage(messages.checkLogoTypeLabel),
        state: isLogoTypeAccepted(logo) ? "pass" : "fail",
        detail: isLogoTypeAccepted(logo) ? undefined : logo.name,
    },
    {
        id: "logo-size",
        label: intl.formatMessage(messages.checkLogoSizeLabel, { limit: formatMegabytes(LOGO_MAX_BYTES) }),
        state: isLogoSizeAccepted(logo) ? "pass" : "fail",
        detail: isLogoSizeAccepted(logo) ? undefined : formatMegabytes(logo.size),
    },
];

export const runClientPreChecks = (
    intl: IntlShape,
    file: File,
    logo: File | null,
    buildOutput: WebLensBuildOutputMeasurement | null = null,
): SubmissionCheck[] => {
    const checks: SubmissionCheck[] = [
        {
            id: "package-type",
            label: intl.formatMessage(messages.checkPackageTypeLabel),
            state: isZipPackage(file) ? "pass" : "fail",
            detail: describePackageTypeFailure(intl, file),
        },
        ...buildPackageSizeChecks(intl, file),
    ];

    if (buildOutput) {
        checks.push(buildBudgetCheck(intl, buildOutput));
    }
    if (logo) {
        checks.push(...buildLogoChecks(intl, logo));
    }

    return checks;
};

export const preChecksPassed = (checks: SubmissionCheck[]): boolean => checks.every((c) => c.state === "pass");

// Push-to-device's own size gate: the archive cap is the push pipeline's 50 MiB, not the upload
// ticket's ZIP_MAX_BYTES — an accepted upload the push then refuses would waste the whole upload.
export const isPushArchiveSizeAccepted = (file: File): boolean => file.size > 0 && file.size <= PUSH_ARCHIVE_MAX_BYTES;

export const describePushArchiveSizeFailure = (intl: IntlShape, file: File): string | undefined => {
    if (file.size === 0) {
        return intl.formatMessage(messages.checkPackageEmptyDetail);
    }
    return isPushArchiveSizeAccepted(file) ? undefined : formatMegabytes(file.size);
};

/**
 * The icon dimension gate, same open-when-unmeasured philosophy as the build-output budget: it
 * only closes on a MEASURED overshoot, so it can never falsely block an icon we couldn't read.
 * lens-bundle enforces the same bound at conversion (LOGO_MAX_DIMENSION), where the rejection
 * classifies as INVALID_LENS_ICON — a shared platform reason whose message reads dev-flavored on
 * the push rail, hence catching it before any bytes upload.
 */
export const isLogoDimensionAccepted = (measurement: WebLensLogoMeasurement | null): boolean =>
    !measurement || (measurement.widthPx <= LOGO_MAX_DIMENSION_PX && measurement.heightPx <= LOGO_MAX_DIMENSION_PX);

/** The dimension row exists only once a measurement exists — same conditional idiom as the icon rows. */
export const buildLogoDimensionCheck = (intl: IntlShape, measurement: WebLensLogoMeasurement): SubmissionCheck => ({
    id: "logo-dimension",
    label: intl.formatMessage(messages.checkLogoDimensionLabel, { limit: LOGO_MAX_DIMENSION_PX }),
    state: isLogoDimensionAccepted(measurement) ? "pass" : "fail",
    detail: isLogoDimensionAccepted(measurement) ? undefined : `${measurement.widthPx} × ${measurement.heightPx} px`,
});

/**
 * The push page's pre-checks: the same package/icon rows as submission, with the push archive cap
 * in place of the ticket cap and NO build-output budget row — push-to-device runs the relaxed
 * 50 MiB validator, so the publish path's 8 MiB budget would falsely block a previewable build.
 * The icon dimension row is push-only for now: only here does the conversion run inside the call,
 * so only here does its INVALID_LENS_ICON rejection land on this rail.
 */
export const runPushClientPreChecks = (
    intl: IntlShape,
    file: File,
    logo: File | null,
    logoMeasurement: WebLensLogoMeasurement | null = null,
): SubmissionCheck[] => {
    const checks: SubmissionCheck[] = [
        {
            id: "package-type",
            label: intl.formatMessage(messages.checkPackageTypeLabel),
            state: isZipPackage(file) ? "pass" : "fail",
            detail: describePackageTypeFailure(intl, file),
        },
        {
            id: "package-size",
            label: intl.formatMessage(messages.checkPackageSizeLabel, {
                limit: formatMegabytes(PUSH_ARCHIVE_MAX_BYTES),
            }),
            state: isPushArchiveSizeAccepted(file) ? "pass" : "fail",
            detail: describePushArchiveSizeFailure(intl, file),
        },
    ];

    if (logo) {
        checks.push(...buildLogoChecks(intl, logo));
        if (logoMeasurement) {
            checks.push(buildLogoDimensionCheck(intl, logoMeasurement));
        }
    }

    return checks;
};
